import {Divider} from '@genestack/ui';
import classNames from 'classnames';
import React from 'react';

import {FindUsagesEditorEvent} from '../../../../../webeditor/biokb-editor-adapters';

import {OntologyViewCaption} from './ontology-view-caption';
import {OntologyViewFooter} from './ontology-view-footer';
import styles from './ontology-view.module.css';
import {useOntologyQuery} from './requests/use-ontology-query';
import {OntologyElement, useOntologyElementQuery} from './requests/use-ontology-element-query';
import {DSLPreloader} from '../../../../components/preloader/dsl-preloader';
import {NodeView} from './node-view/node-view';

interface Props {
    mode: 'explore' | 'edit';
    document: EditModeDoc | ViewModeDoc;
    /** versionId is meaningful only in Explore mode */
    versionId?: number | null;
    nodeId?: string;
    onNavigate: (accession: string, nodeId: string | null) => void;
    onFindUsages: (event: FindUsagesEditorEvent) => void;
    className?: string;
    children?: (ontologyElementInfo?: OntologyElement) => React.ReactNode;
}

export function OntologyView(props: Props) {
    const ontologyQuery = useOntologyQuery({
        isExploreMode: props.mode === 'explore',
        documentId: props.document.id,
        accession: props.document.accession
    });

    const nodeQuery = useOntologyElementQuery({
        documentId: props.document.id,
        versionId: props.versionId,
        nodeId: props.nodeId,
        accession: props.document.accession,
        isExploreMode: props.mode === 'explore'
    });

    const handleNavigate = (accession: string | null, nodeId: string | null) => {
        props.onNavigate(accession || props.document.accession, nodeId);
    };

    return (
        <>
            {props.children && props.children(nodeQuery.data)}

            <div className={classNames(styles.root, props.className)} data-testid="ontology-view">
                <OntologyViewCaption total={ontologyQuery.data?.totalElements} />

                <Divider variant="transparent" gap={2} />

                {(function () {
                    if (!props.nodeId) {
                        return null;
                    }

                    if (!nodeQuery.data) {
                        return <DSLPreloader show />;
                    }

                    return (
                        <NodeView
                            mode={props.mode}
                            documentInfo={props.document}
                            versionId={props.versionId}
                            nodeId={props.nodeId}
                            onFindUsages={props.onFindUsages}
                            onNavigate={handleNavigate}
                            nodeInfo={nodeQuery.data}
                        />
                    );
                })()}
            </div>

            {ontologyQuery.data && (
                <OntologyViewFooter timestamp={ontologyQuery.data?.lastImported} />
            )}
        </>
    );
}
